import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import { Alert, Switch, SxProps, Typography, useMediaQuery, useTheme } from '@mui/material';

// OL
import OlSourceVector from 'ol/source/Vector';
import OlPoint from 'ol/geom/Point';
import OlLayerVector from 'ol/layer/Vector';
import { Snap } from 'ol/interaction';
import { Geometry } from 'ol/geom';
import OlFeature from 'ol/Feature';
import { transform } from 'ol/proj';
import { Collection as OlCollection } from 'ol';
import OlBaseLayer from 'ol/layer/Base';

// Custom Components
import useApi from '@/lib/api/useApi';
import MapContext, { MapContextType } from '@/context/MapContext/MapContext';
import LoaderContext from '@/context/LoaderContext/LoaderContext';
import { generateDXF } from '@/lib/dxfGenerator';
import { routePointStyle } from '@/components/Map/mapStyles';
import exportCSV from '@/lib/exportCSV';
import ProfileChart from '@/ui/Charts/ProfileChart';
import { GridContainer, GridItem } from '@/ui/Grid';
import { FloatNumberControl } from '@/controls';

// Types
import { DrawingType } from '@/@types/common';
import { IFieldNumeric } from '@/@types/models/model';
import { checkIfLayerVisible, flattenLayers } from '@/lib/olHelpers';

export interface IMeasuresPane {
  changeDrawType: (type: DrawingType) => void;
  handleEraseMeasurements: () => void;
  drawType: DrawingType | undefined;
  elevationPointsFeatures: any;
  measurementsSource: any;
  profileSettings: any;
  layersCollection: OlCollection<OlBaseLayer>;
}
const LINE = 'LineString';
const POLY = 'Polygon';
const PT = 'Point';
const PROFILE = 'Profile';
const RADIUS = 'Circle';

type Point2D = [number, number];
type Point3D = [number, number, number];

const buttonStyle: SxProps = {
  width: '100%',
  alignItems: 'flex-start',
};

const floatField: IFieldNumeric = {
  title: '',
  source: 'preciznost_profila',
  ttoken: '',
  type: 'numeric',
};

function checkIfProfileExist(features: any): boolean {
  return features.some((feature: any) => 'line_uuid' in feature.getProperties());
}

const MeasuresPane = (props: IMeasuresPane) => {
  const mapContext = useContext(MapContext) as MapContextType;
  const { toggleLoading } = useContext(LoaderContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { changeDrawType, handleEraseMeasurements, drawType, elevationPointsFeatures, measurementsSource, profileSettings, layersCollection } = props;

  const exportEnabled = useMemo(() => checkIfProfileExist(elevationPointsFeatures), [elevationPointsFeatures]);

  const [series, setSeries] = useState<{
    name: string;
    data: number[][];
  }>();
  const [profileData, setData] = useState<any>(null);
  const [precision, setPrecision] = useState<number>(0.15);
  const [precisionDisplay, setPrecisionDisplay] = useState<string>('0,15');
  const [floatHelperText, setFloatHelperText] = useState<string>('');
  const [minPrecision, setMinPrecision] = useState<number>(0.1);
  const pointOnRouteSourceRef = useRef<OlSourceVector<OlFeature<OlPoint>>>(new OlSourceVector({}));
  const [pointOnRouteLayer] = useState<OlLayerVector<OlSourceVector<OlFeature<OlPoint>>>>(
    new OlLayerVector({
      source: pointOnRouteSourceRef.current,
      style: routePointStyle,
    })
  );
  const [redrawProfileToken, setRedrawToken] = useState<boolean>(false);
  const [snapEnabled, setSnapEnabled] = useState<boolean>(true);

  const getVectorLayers = (): OlLayerVector<OlSourceVector<OlFeature<Geometry>>>[] => {
    if (mapContext.map) {
      const mc = mapContext.map;
      // find Vector layers in map.getLayers
      const vectorLayers = mc
        .getAllLayers()
        // scuffed but all functional non data layers should start with _
        .filter(
          (layer) =>
            layer instanceof OlLayerVector &&
            layer.get('id') &&
            !(layer.get('id') as string).startsWith('_') &&
            checkIfLayerVisible(mapContext, layer)
        ) as OlLayerVector<OlSourceVector<OlFeature<Geometry>>>[];
      // console.log(vectorLayers);

      return vectorLayers as OlLayerVector<OlSourceVector<OlFeature<Geometry>>>[];
    }
    return [];
  };
  const vectorLayers = useMemo(() => getVectorLayers(), [mapContext.map, mapContext.layerVisibility]);


  // profileSettings ? profileSettings.width.toFixed(2) : null;
  // why was this here?

  const apiInstance = useApi();

  useEffect(() => {
    const path = 'profile/params';

    toggleLoading(true);
    apiInstance
      .get(path)
      .then((resp) => {
        if (resp.success) {
          const data = resp.data as any;
          if (!data) return;
          const value = Math.round(data.width * 100) / 100;
          setMinPrecision(value);
          setPrecision(value);
          setPrecisionDisplay(value.toString());
        }
      })
      .finally(() => {
        toggleLoading(false);
      });
  }, []);

  useEffect(() => {
    if (mapContext.map) {
      pointOnRouteLayer.setMap(mapContext.map);
    }
  }, [mapContext.map]);

  useEffect(() => {
    if (exportEnabled && drawType === 'Profile') {
      const sendingData: any = [];
      let firstLineUuid: string | null = null;

      elevationPointsFeatures?.forEach((feature: any) => {
        const featureProps = feature.getProperties();
        if (featureProps) {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { alt, dh, line_uuid } = featureProps;

          if (firstLineUuid == null) firstLineUuid = line_uuid;
          if (sendingData[line_uuid]) {
            sendingData[line_uuid] = [
              ...sendingData[line_uuid],
              {
                alt,
                dh,
                line_uuid,
                coordinates: featureProps.geometry.getCoordinates(),
              },
            ];
          } else {
            sendingData[line_uuid] = [
              {
                alt,
                dh,
                line_uuid,
                coordinates: featureProps.geometry.getCoordinates(),
              },
            ];
          }
        }
      });

      /*
        Sending data should now look like 
          [
            [ point1, point2, point3...]
          ]

          later on we can make it 
          [
            line1_id: [ point1, point2],
            line2_id: [ point1, point2, point3...]
          ]
          when we decide to show mutiple routes on chart
          
          where each point contains:
            alt: altitude
            dh: altitude change from the prevoius point
            coordinates: x,y of the feature
            line_uuid: used to group points for a line
            
          backend will only need coordinates from the line the user wants to draw on the chart
          its possible to use different styles for drawing each route and drawing multipe routes to chart
          
          */

      // API CALL TO BACKEND WITH LINES
      const path = 'profile/generate';
      if (firstLineUuid == null) return;

      const myLine = sendingData[firstLineUuid];
      let wkt = 'LINESTRING(';
      wkt += myLine
        .map((p: any) => {
          const coords = p.coordinates;
          return `${coords[0]} ${coords[1]}`;
        })
        .join(',');
      wkt += ')';

      toggleLoading(true);
      apiInstance
        .post(path, { line_wkt: wkt, precision })
        .then((resp) => {
          if (resp.success) {
            const data = resp.data as any;
            if (!data) return;
            setData(data);
            setSeries({
              name: '',
              data: data.route.map((c: number[]) => [Math.round(c[0]), c[1].toFixed(2)]) as number[][],
            });
          }
        })
        // .catch((e) => {
        //    console.log(e);
        // })
        .finally(() => {
          toggleLoading(false);
        });
    }
  }, [elevationPointsFeatures, drawType, redrawProfileToken]);

  useEffect(() => {
    if (!mapContext.map) return undefined;
    const mc = mapContext.map;

    const existingSnaps: Snap[] = [];
    mc.getInteractions().forEach((interaction) => {
      if (interaction instanceof Snap) existingSnaps.push(interaction);
    });
    existingSnaps.forEach((snap) => mc.removeInteraction(snap));

    if (!snapEnabled || isMobile) return undefined;

    // Defer so DrawInteraction's useEffect (which re-adds Draw on drawType change)
    // runs first — Snap must be added after Draw in the interaction stack.
    // hacky piece of shit it works tho
    const id = setTimeout(() => {
      vectorLayers.forEach((layer) => {
        const source = layer.getSource();
        if (source) mc.addInteraction(new Snap({ source }));
      });
    }, 0);

    return () => clearTimeout(id);
  }, [snapEnabled, isMobile, drawType, vectorLayers]);

  const handleSnapToggle = () => {
    setSnapEnabled((prev) => !prev);
  };

  const handleChartMouseEnter = (e: any, chart: any, options: any) => {
    const ind = options.dataPointIndex;
    const m = chart.data.twoDSeriesX[ind];
    const last = chart.data.twoDSeriesX[chart.data.twoDSeriesX.length - 1];
    const mrel = m / last;

    const geom = measurementsSource?.getFeatures()[0]?.getGeometry(); // selectedRoute?.feature.getGeometry();
    if (geom) {
      const p = geom.getCoordinateAt(mrel);
      const pFeatures = pointOnRouteSourceRef.current.getFeatures();
      if (Array.isArray(pFeatures) && p) {
        if (pFeatures.length === 0) {
          pointOnRouteSourceRef.current.addFeature(
            new OlFeature<OlPoint>({
              geometry: new OlPoint(p),
            })
          );
        } else {
          const pf = pFeatures[0];
          pf.setGeometry(new OlPoint(p));
        }
      }
    }
  };

  const handleChartMouseLeave = () => {
    pointOnRouteSourceRef.current.clear();
  };

  const createXmlString = (lines: number[][][]): string => {
    let result =
      '<gpx xmlns="http://www.topografix.com/GPX/1/1" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd" version="1.1" creator="runtracker"><metadata/><trk><name></name><desc></desc>';
    result += lines.reduce((accum, curr) => {
      let segmentTag = '<trkseg>';
      segmentTag += curr.map((point) => `<trkpt lat="${point[1]}" lon="${point[0]}" ><ele>${point[2]}</ele></trkpt>`).join('\n');
      segmentTag += '</trkseg>';

      return accum + segmentTag;
    }, '');
    result += '</trk></gpx>';
    return result;
  };

  const handleDownloadGPX = () => {
    if (!profileData) return;
    const profileData4326 = profileData.points
      .flat()
      .map((coord3857: any, index: number) => [
        ...transform(coord3857, 'EPSG:3857', 'EPSG:4326'),
        parseFloat(series?.data[index][1].toString() || '0'),
      ]);
    const xml = createXmlString([profileData4326]);
    const url = `data:text/json;charset=utf-8,${encodeURIComponent(xml)}`;
    const link = document.createElement('a');
    link.download = `profile.gpx`;
    link.href = url;
    document.body.appendChild(link);
    link.click();
  };

  const handleDownloadDXF = () => {
    if (!profileData) return;
    const pointsXY: Point2D[] = profileData.points.flat();

    // Map through each point and add relevant altitude data to it
    const pointsXYZ: Point3D[] = pointsXY.map((point: any, index: number) => {
      const zVal = parseFloat(series?.data[index][1].toString() || '0');
      const pointXYZ = point.concat(zVal);
      return pointXYZ;
    });

    const dxfContent = generateDXF(pointsXYZ);
    const blob = new Blob([dxfContent], {
      type: 'application/dxf;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'profile.dxf';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCSV = () => {
    if (!profileData) return;
    let profileCsvContent = 'x, y, z\n';
    profileData.points.flat().forEach((dataPoint: any, index: number) => {
      profileCsvContent += `${dataPoint[0]}, ${dataPoint[1]}, ${parseFloat(series?.data[index][1].toString() || '0')}\n`;
    });
    // console.log(profileCsvContent);
    if (profileCsvContent) {
      exportCSV(profileCsvContent, 'profile.csv');
    }
  };

  const handleChartDataMounted = () => {
    // console.log("Data mounted");
    // toggleLoading(false);
  };

  const handleChange = (value: string | number | null) => {
    if (typeof value === 'number') {
      setPrecisionDisplay(value.toString());
    } else if (typeof value === 'string') {
      setPrecisionDisplay(value.replace(',', '.'));
    } else if (value === null) {
      setPrecisionDisplay('');
    }
  };

  const handleBlur = () => {
    // const value = precision;
    const numValue = parseFloat(precisionDisplay);
    if (Number.isNaN(numValue) || numValue < minPrecision) {
      setFloatHelperText('map:measure.minimum_precision');
      setPrecision(minPrecision);
      setPrecisionDisplay(minPrecision.toString());
    } else {
      setFloatHelperText('');
      setPrecision(numValue);
    }
  };

  const handleKeyDown = (evt: any) => {
    if (evt.key === 'Enter') {
      handleBlur();
      setRedrawToken((prev: boolean) => !prev);
    }
    if (evt.key === 'Escape') {
      setPrecisionDisplay('');
    }
  };

  const SNAP_CHIP_LIMIT = 4;
  // vectorlayer doesnt contian title. can we get it from layersCollection using id? maybe we should add title to layer properties when creating it in layer tree

  const visibleLayerNames = vectorLayers.map((vl) => {
    const id = vl.get('id');
    const layerInCollection = flattenLayers(layersCollection.getArray(), 5).find((l) => l.get('id') === id);
    return layerInCollection ? layerInCollection.get('title') || layerInCollection.get('id') || '?' : '?';
  });
  const shownChips = visibleLayerNames.slice(0, SNAP_CHIP_LIMIT);
  const hiddenCount = visibleLayerNames.length - shownChips.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>

      {/* ── Section 1: Measurement tools ── */}
      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {t('map:measure.section.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t(isMobile ? 'map:measure.description_mobile' : 'map:measure.description')}
        </Typography>
        <GridContainer spacing={1}>
          <GridItem size={6}>
            <Button
              value="length"
              color="primary"
              onClick={() => changeDrawType(LINE)}
              variant={drawType === LINE ? 'contained' : 'outlined'}
              startIcon={<i className="fas fa-ruler-horizontal" />}
              sx={buttonStyle}
            >
              {t('map:measure.buttons.line')}
            </Button>
          </GridItem>
          <GridItem size={6}>
            <Button
              value="area"
              color="primary"
              onClick={() => changeDrawType(POLY)}
              variant={drawType === POLY ? 'contained' : 'outlined'}
              startIcon={<i className="fas fa-vector-square" />}
              sx={buttonStyle}
            >
              {t('map:measure.buttons.area')}
            </Button>
          </GridItem>
          <GridItem size={6}>
            <Button
              value="point"
              color="primary"
              onClick={() => changeDrawType(PT)}
              variant={drawType === PT ? 'contained' : 'outlined'}
              startIcon={<i className="fas fa-map-marker-alt" />}
              sx={buttonStyle}
            >
              {t('map:measure.buttons.point')}
            </Button>
          </GridItem>
          <GridItem size={6}>
            <Button
              value="radius"
              color="primary"
              onClick={() => changeDrawType(RADIUS)}
              variant={drawType === RADIUS ? 'contained' : 'outlined'}
              startIcon={<i style={{ display: 'flex', height: '20px' }} className="fg-circle-o" />}
              sx={buttonStyle}
            >
              {t('map:measure.buttons.radius')}
            </Button>
          </GridItem>
        </GridContainer>
      </Box>

      {/* ── Drawing hint ── */}
      {drawType ? (
        <Alert severity="info" variant="outlined">
          {drawType === PT
            ? t(isMobile ? 'map:measure.drawing_hint.point_mobile' : 'map:measure.drawing_hint.point')
            : drawType === RADIUS
              ? t(isMobile ? 'map:measure.drawing_hint.radius_mobile' : 'map:measure.drawing_hint.radius')
              : t(isMobile ? 'map:measure.drawing_hint.default_mobile' : 'map:measure.drawing_hint.default')}
        </Alert>
      ) : null}

      {/* ── Section 2: Snap card ── */}
      {!isMobile && vectorLayers.length > 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" fontWeight={600}>
              {t('map:measure.snap.title')}
            </Typography>
            <Switch checked={snapEnabled} onChange={handleSnapToggle} color="primary" size="small" />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 1 }}>
            {t('map:measure.snap.description')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {shownChips.map((name) => (
              <Chip key={`snap-chip-${name}`} label={t(name)} size="small" variant="outlined" />
            ))}
            {hiddenCount > 0 && (
              <Tooltip title={visibleLayerNames.slice(SNAP_CHIP_LIMIT).map((n) => t(n))
              .join(', ')} arrow>
                <Chip label={`+${hiddenCount}`} size="small" />
              </Tooltip>
            )}
          </Box>
        </Paper>
      ) : null}

      {/* ── Section 3: Elevation profile ── */}
      {profileSettings !== undefined && (
        <Box>
          <Divider textAlign="left" sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('map:measure.profile.title')}
            </Typography>
          </Divider>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('map:measure.profile.description')}
          </Typography>
          <Button
            value="elevation"
            color="primary"
            onClick={() => changeDrawType(PROFILE)}
            variant={drawType === PROFILE ? 'contained' : 'outlined'}
            startIcon={<i className="fas fa-chart-area" />}
            sx={buttonStyle}
          >
            {t('map:measure.buttons.elevation')}
          </Button>
        </Box>
      )}

      {/* ── Section 4: Profile controls ── */}
      {drawType === 'Profile' ? (
        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {t('map:measure.precision')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <FloatNumberControl
              sx={{ width: '40%' }}
              variant="standard"
              field={floatField}
              value={precisionDisplay}
              onChange={handleChange}
              formMode="form"
              controlMode="edit"
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              helperText={t(floatHelperText, { precision: minPrecision.toString() })}
              FormHelperTextProps={{ error: true }}
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setRedrawToken((prev: boolean) => !prev)}
              startIcon={<i className="fas fa-refresh" />}
              disabled={!exportEnabled}
              sx={{ mt: 0.5 }}
            >
              {t('map:measure.buttons.draw_profile')}
            </Button>
          </Box>
        </Box>
      ) : null}

      {/* ── Section 5: Chart + exports ── */}
      {exportEnabled && drawType === 'Profile' ? (
        <Box>
          <ProfileChart
            title={t('map:route.headings.profile')}
            // @ts-ignore
            series={series}
            handleChartMouseEnter={handleChartMouseEnter}
            handleChartMouseLeave={handleChartMouseLeave}
            handleChartDataMounted={handleChartDataMounted}
          />
          <GridContainer spacing={1} sx={{ mt: 0.5 }}>
            <GridItem size={4}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleDownloadCSV}
                startIcon={<i className="fas fa-download" />}
                sx={buttonStyle}
                size="small"
              >
                CSV
              </Button>
            </GridItem>
            <GridItem size={4}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleDownloadDXF}
                startIcon={<i className="fas fa-download" />}
                sx={buttonStyle}
                size="small"
              >
                DXF
              </Button>
            </GridItem>
            <GridItem size={4}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleDownloadGPX}
                startIcon={<i className="fas fa-download" />}
                sx={buttonStyle}
                size="small"
              >
                GPX
              </Button>
            </GridItem>
          </GridContainer>
        </Box>
      ) : null}

      {/* ── Section 6: Erase ── */}
      <Box>
        <Divider sx={{ mb: 1.5 }} />
        <Button
          variant="outlined"
          color="primary"
          disabled={measurementsSource?.getFeatures().length === 0}
          onClick={() => handleEraseMeasurements()}
          startIcon={<i className="fas fa-trash" />}
          sx={buttonStyle}
        >
          {t('map:measure.buttons.erase')}
        </Button>
      </Box>

    </Box>
  );
};

export default MeasuresPane;
